import { useState, useEffect } from "react";
import { Button } from "react-blueprint-library";
import UploadModal from "./UploadModal";
import { uploadFiles } from "../../../../api/uploadDownloadFiles";
import { insertFilenetDocuments } from "../../../../api/FilenetDocuments";
import { updateDocumentRequestTypeStatus } from "../../../../api/DocumentRequests";
import svgs, { svgType } from "../../../../assets/svgs";
import { useSelector } from "react-redux";
import Toast from "../../../SharedComponents/Toast";

const UploadDocument = ({
  oktaSubId,
  contractid,
  contracttype,
  startApplicationButtonText = "",
  setIsDocRequest = () => {},
  isRequested = false,
  isDropdownItem = false,
  onDocumentsUploaded = () => {},
  isDocReqUploadModalOpen = false,
  setIsDocReqUploadModalOpen = () => {},
  onShowConfirmationModal = () => {},
  setContractDataUpload = () => {},
  hasReqDocsForContract = false,
  hasSSN = false,
}) => {
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(
    isDocReqUploadModalOpen || isDropdownItem || false,
  );
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [requestedDocsState, setRequestedDocs] = useState([]);
  const [showToast, setShowToast] = useState(false);
  const [toastMsg, setToastMsg] = useState("");

  const requestedDocs = useSelector(
    (state) => state.uploadedReqDocs?.reqdocs || [],
  );

  useEffect(() => {
    if (isRequested && contractid) {
      const filtered = requestedDocs.filter(
        (doc) =>
          doc.contractid &&
          String(doc.contractid) === String(contractid) &&
          doc.requestStatus === "requested" &&
          !doc.guid,
      );

      setRequestedDocs(filtered);
    }
  }, [isRequested, contractid, requestedDocs]);

  const openModal = () => {
    hasReqDocsForContract ? setIsDocRequest(true) : setIsUploadModalOpen(true);
  };

  const clearForm = () => {
    setUploadedFiles([]);
  };

  const closeUploadModal = () => {
    setIsDocRequest(false);
    setIsUploadModalOpen(false);
    setContractDataUpload({});

    if (setIsDocReqUploadModalOpen) {
      setIsDocReqUploadModalOpen(false);
    }

    clearForm();
  };

  // Called from UploadFileSection
  const handleFilesChange = (files) => {
    setUploadedFiles(files);
  };

  // Only enable submit if all files have a type selected and at least one file exists
  const canSubmit =
    uploadedFiles.length > 0 &&
    uploadedFiles.every((f) => f.type && f.type !== "");

  const handleUpload = async () => {
    // Prepare multipart form data
    const formData = new FormData();

    uploadedFiles.forEach((f) => {
      formData.append("files", f.file);
      formData.append("types", f.type); // Use the same key for all types
    });

    try {
      setIsLoading(true);
      const uploadFilesData = await uploadFiles({
        oktaSubId,
        formData,
      });

      const { success, uploaded: documents } = uploadFilesData;

      if (success) {
        const errorObj = documents.find((obj) => obj.error);
        const errorMessage = errorObj ? errorObj.error : null;

        if (!errorMessage) {
          await insertFilenetDocuments(
            oktaSubId,
            documents,
            contractid,
            isRequested,
          );

          // Build array of { requestid, typeid } for uploaded files
          const items = uploadedFiles
            .map((f) => {
              const matchingRequest = requestedDocsState.find(
                (doc) => doc.typedesc === f.typeDesc,
              );
              return matchingRequest
                ? {
                    requestid: matchingRequest.reqDocId,
                    typeid: matchingRequest.type,
                  }
                : null;
            })
            .filter(Boolean);

          if (items.length > 0) {
            await updateDocumentRequestTypeStatus(oktaSubId, "uploaded", items);
          }

          onDocumentsUploaded();
        } else {
          console.error(errorMessage);
          setIsLoading(false);
          clearForm();
          closeUploadModal();
          setToastMsg(errorMessage);
          setShowToast(true);
          throw new Error(`Upload failed: ${errorMessage}`);
        }
      }

      setIsLoading(false);

      closeUploadModal();
      clearForm();
      onShowConfirmationModal();
    } catch (err) {
      console.error(err);
      clearForm();
    }
  };

  if (isDocReqUploadModalOpen) {
    return (
      <>
        <UploadModal
          isOpen={isUploadModalOpen || isRequested}
          onClose={closeUploadModal}
          isRequested={isRequested || false}
          onUpload={handleUpload}
          files={uploadedFiles}
          onFilesChange={handleFilesChange}
          canSubmit={canSubmit}
          isLoading={isLoading}
          oktaSubId={oktaSubId}
          requestedDocs={requestedDocsState}
          contracttype={contracttype}
        />
      </>
    );
  }

  return (
    <>
      {!isDropdownItem ? (
        <Button
          id="CandidateEndorsedContractUploadBtn"
          variant="link"
          style={{
            marginBottom: "10px",
            ...(startApplicationButtonText === "Documents" && {
              width: "165px",
              justifyContent: "center",
              alignItems: "center",
              display: "flex",
            }),
          }}
          onClick={openModal}
          disabled={!hasSSN}
        >
          <div style={{ display: "flex", alignItems: "center" }}>
            Upload
            <span
              style={{
                width: 20,
                height: 20,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginLeft: 4,
              }}
            >
              {hasSSN
                ? svgs[svgType.uploadIcon]
                : svgs[svgType.uploadIconDisabled]}
            </span>
          </div>
        </Button>
      ) : null}

      {(isUploadModalOpen || isRequested || isDropdownItem) && (
        <UploadModal
          isOpen={true}
          onClose={closeUploadModal}
          isRequested={isRequested || false}
          onUpload={handleUpload}
          files={uploadedFiles}
          onFilesChange={handleFilesChange}
          canSubmit={canSubmit}
          isLoading={isLoading}
          oktaSubId={oktaSubId}
          requestedDocs={requestedDocsState}
          contracttype={contracttype}
        />
      )}

      <Toast
        closeToast={() => setShowToast(false)}
        showToast={showToast}
        toastText={toastMsg}
      />
    </>
  );
};

export default UploadDocument;
